// Varian warna kendaraan Dealer tanpa generate ulang: sprite kendaraan dirender ke kanvas offscreen, lalu piksel yang se-hue dengan
// warna bodi dominan (jenuh, bukan roda/kaca/lampu) dipetakan ke hue+saturasi warna pilihan sambil mempertahankan terang-gelapnya
// (shading tetap). Hasil di-cache per (item, warna, ukuran). Dipakai di panggung Dealer dan zona parkir rumah.
import { compileMall } from "./mallRegistry";
import { lukisPerabotMall } from "../components/mall/MallPreview";

export const WARNA_KENDARAAN = [
  { key: "asli", nama: "Warna asli", hex: null },
  { key: "merah", nama: "Merah bata", hex: "#b2342c" },
  { key: "biru", nama: "Biru Jawsel", hex: "#2e58c4" },
  { key: "putih", nama: "Putih gading", hex: "#ece6d6" },
  { key: "hitam", nama: "Hitam arang", hex: "#2b2b30" },
  { key: "hijau", nama: "Hijau zaitun", hex: "#6b7a3a" },
  { key: "kuning", nama: "Kuning mustard", hex: "#d9a21b" },
  { key: "oranye", nama: "Oranye", hex: "#e07a2a" },
  { key: "perak", nama: "Abu perak", hex: "#a7adb5" },
];
export const WARNA_HEX = new Set(WARNA_KENDARAAN.filter(w => w.hex).map(w => w.hex));

function rgb2hsl(r, g, b) {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2;
  if (max === min) return [0, 0, l];
  const d = max - min, s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = max === r ? (g - b) / d + (g < b ? 6 : 0) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return [h * 60, s, l];
}
function hsl2rgb(h, s, l) {
  const c = (1 - Math.abs(2 * l - 1)) * s, x = c * (1 - Math.abs(((h / 60) % 2) - 1)), m = l - c / 2;
  const [r, g, b] = h < 60 ? [c, x, 0] : h < 120 ? [x, c, 0] : h < 180 ? [0, c, x] : h < 240 ? [0, x, c] : h < 300 ? [x, 0, c] : [c, 0, x];
  return [(r + m) * 255, (g + m) * 255, (b + m) * 255];
}
const hex2rgb = hex => [parseInt(hex.slice(1, 3), 16), parseInt(hex.slice(3, 5), 16), parseInt(hex.slice(5, 7), 16)];
const selisihHue = (a, b) => { const d = Math.abs(a - b) % 360; return d > 180 ? 360 - d : d; };

// Ganti warna bodi pada ImageData. Bodi dominan dicari dari histogram (hue 10° × kelas saturasi); bodi jenuh → piksel se-hue & jenuh
// dipetakan; bodi netral (putih/perak/hitam) → piksel netral dengan terang mirip. Roda/kaca/lampu (beda hue atau terlalu gelap) tetap.
function catUlang(img, hex) {
  const px = img.data, [tr, tg, tb] = hex2rgb(hex), [th, ts, tl] = rgb2hsl(tr, tg, tb);
  const hist = new Float32Array(72), sumL = new Float32Array(72);   // 0..35 jenuh per hue, 36..71 netral per kelas terang
  for (let i = 0; i < px.length; i += 4) {
    if (px[i + 3] < 120) continue;
    const [h, s, l] = rgb2hsl(px[i], px[i + 1], px[i + 2]);
    if (l < 0.12 || l > 0.97) continue;
    const k = s >= 0.4 ? Math.floor(h / 10) % 36 : l > 0.3 ? 36 + Math.floor(l * 35.99) : -1; if (k >= 0) { hist[k] += 1; sumL[k] += l; }
  }
  let dom = 0; for (let k = 1; k < 72; k++) if (hist[k] > hist[dom]) dom = k;
  const bodiJenuh = dom < 36, hueDom = dom * 10 + 5, lDom = hist[dom] ? sumL[dom] / hist[dom] : 0.5, netral = ts < 0.12;
  for (let i = 0; i < px.length; i += 4) {
    if (px[i + 3] < 120) continue;
    const [h, s, l] = rgb2hsl(px[i], px[i + 1], px[i + 2]);
    // bodi terang (krem/pastel): sisi yang teduh saturasinya turun → ikut dicat bila se-hue; bodi gelap: hanya piksel jenuh (kaca abu-biru aman)
    const kena = bodiJenuh ? ((s >= 0.4 || (lDom > 0.7 && s >= 0.08 && Math.abs(l - lDom) < 0.45)) && l > 0.12 && l < 0.97 && selisihHue(h, hueDom) <= 28) : (s < 0.4 && l > 0.3 && Math.abs(l - lDom) < 0.3);
    if (!kena) continue;   // kaca abu-biru, krom, roda, lampu tetap
    const rel = (l - lDom) * (bodiJenuh ? 0.7 : 0.6);   // shading relatif terhadap terang bodi dominan
    const l2 = Math.max(0.05, Math.min(0.95, tl + rel)), s2 = netral ? ts : Math.min(1, ts * 0.9 + (bodiJenuh ? s * 0.2 : 0.1));
    const [r, g, b] = hsl2rgb(th, s2, l2);
    px[i] = r; px[i + 1] = g; px[i + 2] = b;
  }
  return img;
}

const CACHE = new Map();
// Sprite kendaraan (opsional dicat) pada ukuran tile T; mengembalikan {canvas, ax, ay} dengan (ax, ay) = pusat tapak di dalam kanvas
export function spriteKendaraan(item, T, hex) {
  const Tq = Math.max(8, Math.round(T)), key = `${item.id}|${hex || "asli"}|${Tq}`;
  if (CACHE.has(key)) return CACHE.get(key);
  const w = item.w || 1, d = item.d || 1, lebar = Math.ceil((w + d) * (Tq / 2)) + 8, tinggi = Math.ceil((w + d) * (Tq / 4) + Tq * 0.8) + 8;
  const c = document.createElement("canvas"), dpr = Math.min(2, window.devicePixelRatio || 1);
  c.width = Math.ceil(lebar * dpr); c.height = Math.ceil(tinggi * dpr);
  const ctx = c.getContext("2d"); ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  const ax = lebar / 2, ay = tinggi - 4 - ((w + d) * (Tq / 4)) / 2;
  const fn = compileMall(item.paintCode, item.jenis);
  if (fn) lukisPerabotMall(ctx, fn, w, d, ax, ay, Tq, 0, false);
  if (hex) { try { ctx.putImageData(catUlang(ctx.getImageData(0, 0, c.width, c.height), hex), 0, 0); } catch { /* kanvas tercemar: biarkan warna asli */ } }
  const out = { canvas: c, ax, ay, dpr };
  if (CACHE.size > 60) CACHE.delete(CACHE.keys().next().value);
  CACHE.set(key, out); return out;
}
// Gambar kendaraan dengan pusat tapak di (sx, sy) layar
export function gambarKendaraan(ctx, item, sx, sy, T, hex) {
  const s = spriteKendaraan(item, T, hex);
  ctx.drawImage(s.canvas, sx - s.ax, sy - s.ay, s.canvas.width / s.dpr, s.canvas.height / s.dpr);
}
